import { useState } from 'react'
import { BlastAtmosphere } from '../components/BlastAtmosphere'
import { EmptyState, ErrorState, LoadingState } from '../components/Feedback'
import { Pagination } from '../components/Pagination'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { RankBadge } from '../components/RankBadge'
import { api, ApiError } from '../lib/api'
import {
  compactNumber,
  dateTime,
  duration,
  mapName,
  mode,
  number,
  percentage,
} from '../lib/format'
import { Link, useParams } from '../lib/router'
import { useApi } from '../lib/useApi'

const resultLabels = {
  win: 'Vittoria',
  loss: 'Sconfitta',
  draw: 'Pareggio',
  void: 'Annullata',
} as const

export function PlayerProfilePage() {
  const { uuid = '' } = useParams()
  const [historyPage, setHistoryPage] = useState(1)
  const profile = useApi(`player-${uuid}`, (signal) => api.player(uuid, signal))
  const history = useApi(`matches-${uuid}-${historyPage}`, (signal) =>
    api.matches(uuid, historyPage, signal),
  )

  if (profile.loading) {
    return (
      <main className="section page">
        <LoadingState label="Caricamento profilo" />
      </main>
    )
  }
  if (profile.error) {
    const notFound =
      profile.error instanceof ApiError && profile.error.status === 404
    return (
      <main className="section page">
        {notFound ? (
          <EmptyState
            message="Lo UUID richiesto non appartiene a un giocatore TNTWars conosciuto."
            title="Giocatore non trovato"
          />
        ) : (
          <ErrorState />
        )}
      </main>
    )
  }
  if (!profile.data) return null

  const player = profile.data.data
  const stats = player.statistics

  return (
    <main className="section page profile-page page--charged page--profile">
      <BlastAtmosphere variant="subtle" />
      <aside className="profile-identity panel">
        <div className="profile-render">
          <PlayerAvatar
            size={360}
            username={player.username}
            uuid={player.uuid}
          />
          <span className="profile-render__mmr">{number(player.mmr)} MMR</span>
        </div>
        <p className="eyebrow">Profilo competitivo</p>
        <h1>{player.username}</h1>
        <RankBadge
          rank={player.rank}
          unrankedLabel={`Placement ${player.placementsPlayed}/${player.season.placementMatchesRequired}`}
        />
        <dl className="identity-readout">
          <div>
            <dt>Posizione</dt>
            <dd>{player.rankPosition ? `#${player.rankPosition}` : '—'}</dd>
          </div>
          <div>
            <dt>Peak MMR</dt>
            <dd>{number(player.peakMmr)}</dd>
          </div>
          <div>
            <dt>Stagione</dt>
            <dd>{player.season.id}</dd>
          </div>
        </dl>
        <p className="profile-note">
          Per sfidare un giocatore usa <strong>/tw duel</strong> direttamente
          nel server.
        </p>
      </aside>

      <div className="profile-content">
        <header className="page-heading">
          <p className="eyebrow">Dati stagionali · {player.season.id}</p>
          <h2>Statistiche competitive</h2>
        </header>
        <section className="stat-grid">
          <article className="stat-card stat-card--accent stat-card--rate">
            <span>Win rate</span>
            <strong>{percentage(stats.winRate)}</strong>
          </article>
          <article className="stat-card">
            <span>K/D ratio</span>
            <strong>{stats.kdRatio?.toFixed(2) ?? '—'}</strong>
          </article>
          <article className="stat-card stat-card--gold">
            <span>TNT innescate</span>
            <strong>{compactNumber(stats.tntPrimed)}</strong>
          </article>
          <article className="stat-card">
            <span>Partite</span>
            <strong>{number(stats.matchesPlayed)}</strong>
          </article>
        </section>

        <section className="detail-grid">
          <article>
            <span>Vittorie</span>
            <strong>{number(stats.wins)}</strong>
          </article>
          <article>
            <span>Sconfitte</span>
            <strong>{number(stats.losses)}</strong>
          </article>
          <article>
            <span>Pareggi</span>
            <strong>{number(stats.draws)}</strong>
          </article>
          <article>
            <span>Core distrutti</span>
            <strong>{number(stats.coresDestroyed)}</strong>
          </article>
          <article>
            <span>Eliminazioni</span>
            <strong>{number(stats.eliminations)}</strong>
          </article>
          <article>
            <span>Serie migliore</span>
            <strong>{number(stats.bestStreak)}</strong>
          </article>
        </section>

        <section className="history-panel panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Match history reale</p>
              <h2>Cronologia recente</h2>
            </div>
          </header>
          {history.loading ? <LoadingState label="Caricamento match" /> : null}
          {history.error ? (
            <ErrorState
              message="Il profilo è disponibile, ma la cronologia non può essere caricata."
              title="Cronologia non disponibile"
            />
          ) : null}
          {!history.loading &&
          !history.error &&
          history.data?.data.length === 0 ? (
            <EmptyState
              message="Questo giocatore non ha ancora partite registrate."
              title="Nessun match"
            />
          ) : null}
          <div className="match-list">
            {history.data?.data.map((match) => (
              <article className={`match-row match-row--${match.result}`} key={match.id}>
                <span className="match-row__result">
                  {resultLabels[match.result].charAt(0)}
                </span>
                <div>
                  <strong>
                    {match.opponent ? (
                      <Link to={`/player/${match.opponent.uuid}`}>
                        vs. {match.opponent.username}
                      </Link>
                    ) : (
                      'Avversario non disponibile'
                    )}
                  </strong>
                  <span>
                    {mode(match.mode)} · {mapName(match.mapId)}
                  </span>
                </div>
                <div className="match-row__meta">
                  <strong className={match.mmrDelta >= 0 ? 'positive' : 'negative'}>
                    {match.rated
                      ? `${match.mmrDelta >= 0 ? '+' : ''}${match.mmrDelta} MMR`
                      : 'N/R'}
                  </strong>
                  <span>
                    {duration(match.durationSeconds)} · {dateTime(match.endedAt)}
                  </span>
                </div>
              </article>
            ))}
          </div>
          {history.data ? (
            <Pagination
              onPage={setHistoryPage}
              pagination={history.data.pagination}
            />
          ) : null}
        </section>
      </div>
    </main>
  )
}
